"use client"
import { atom, useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { apiFetch } from '@/lib/apiFetch';

// 定义用户类型
export interface IUserInfo {
  id: string;
  nickName: string;
  phone: string;
  type: number;
  vipStartTime: string;
  vipEndTime: string;
  createTime: string;
  channelCode: string;
  userName: string;
  avatar: string;
  enableFlag: boolean;
  questionsAnswers: number;
  knowledgeLibrary: number;
  articleOperation: number;
  contribute: number;
  copyright: number;
  authFlag: boolean;
  guidancePromptFlag: boolean;
  multipleModelFlag: boolean;
  gistSummaryFlag: boolean;
  click: boolean;
}

export interface IAuthInfo {
  token: string;
  refreshToken: string;
  tokenHead: string;
  expiresIn: number;
}

export interface IUser {
  userInfo: IUserInfo,
  authInfo: IAuthInfo
}

// 定义全局用户 atom
export const userAtom = atom<IUser | null>(null);

export const userInfoAtom = atom<IUserInfo | null>(null);

export const authInfoAtom = atom<IAuthInfo | null>(null);

// 自定义 hook，便于组件使用
export function useUser() {
  return useAtom(userAtom);
}

export function useUserInfo() {
  return useAtom(userInfoAtom);
}

export function useAuthInfo() {
  return useAtom(authInfoAtom);
}


export function useLogin() {

  const [tokenCookie, setTokenCookie] = useCookies(['token']);
  const [userInfoCookie, setUserInfoCookie] = useCookies(['userInfo']);

  const [, setUserInfo] = useUserInfo();
  const [, setToken] = useAuthInfo();
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (userInfoCookie.userInfo) {
      setUserInfo(userInfoCookie.userInfo);
    }
    if (tokenCookie.token) {
      setToken(tokenCookie.token);
    }
  }, [tokenCookie, userInfoCookie, setUserInfo, setToken]);

  async function login(data: { userName: string, password: string }) {
    setLoginLoading(true);
    try {
      const res = await apiFetch('/user/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      const resData = await res.json();
      if (resData.code === 0) {
        setUserInfoCookie('userInfo', resData.data.userInfo, {
          maxAge: 1 * 24 * 60 * 60,
          secure: true,
          sameSite: 'none',
        })
        setTokenCookie('token', resData.data.authInfo, {
          maxAge: 1 * 24 * 60 * 60,
          secure: true,
          sameSite: 'none',
        })
      } else {
        return Promise.reject(resData);
      }
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setLoginLoading(false);
    }
  }

  async function logout() {
    setUserInfoCookie('userInfo', {})
    setTokenCookie('token', {})
    setUserInfo(null)
    setToken(null)
  }

  return {
    login,
    loginLoading,
    logout
  }

}



export function useSendSmsCode() {
  const [sendSmsCodeLoading, setSendSmsCodeLoading] = useState(false);

  async function sendSmsCode(phone: string) {
    setSendSmsCodeLoading(true);
    try {
      const res = await apiFetch(`/common/sendVerifyCode?phone=${phone}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      const resData = await res.json();
      if (resData.code === 0) {
        return Promise.resolve(resData.data);
      } else {
        return Promise.reject(resData);
      }
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setSendSmsCodeLoading(false);
    }
  }

  return {
    sendSmsCode,
    sendSmsCodeLoading
  }
}

export function useRegister() {
  const [registerLoading, setRegisterLoading] = useState(false);

  async function register(data: { phone: string, verificationCode: string, password: string, newpassword: string, activateCode: string }) {
    setRegisterLoading(true);

    try {
      const res = await apiFetch('/user/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      const resData = await res.json();
      if (resData.code === 0) {
        return Promise.resolve(resData.data);
      } else {
        return Promise.reject(resData);
      }
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setRegisterLoading(false);
    }
  }

  return {
    register,
    registerLoading
  }
}
